/**
 * RoleSelectionModal Component
 * Prompts new users to choose: "Are you an artist or a listener?"
 * Artists get access to the Artist Dashboard, Listeners get standard experience
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic2, Headphones, ArrowRight, Sparkles } from 'lucide-react';

const RoleSelectionModal = ({ 
  visible, 
  onSelect, 
  userName 
}) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!selectedRole) return;
    
    setLoading(true);
    try {
      await onSelect(selectedRole);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-[70]"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-lg bg-[#0B0A10] rounded-3xl border border-white/10 overflow-hidden"
              data-testid="role-selection-modal"
            >
              {/* Header */}
              <div className="p-6 pb-0 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center"
                >
                  <Sparkles className="w-8 h-8 text-white" />
                </motion.div>
                
                <h2 
                  className="text-2xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Unbounded' }}
                >
                  Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!
                </h2>
                <p className="text-[#A1A1AA]">
                  How will you be using NLT Radio?
                </p>
              </div>

              {/* Role Options */}
              <div className="p-6 space-y-4">
                {/* Artist Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedRole('artist')}
                  className={`
                    w-full p-5 rounded-2xl border-2 text-left transition-all duration-200
                    ${selectedRole === 'artist' 
                      ? 'border-[#FF007F] bg-[#FF007F]/10' 
                      : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                    }
                  `}
                  data-testid="role-artist-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0
                      ${selectedRole === 'artist' 
                        ? 'bg-[#FF007F]' 
                        : 'bg-gradient-to-br from-[#FF007F]/50 to-[#FF007F]/20'
                      }
                    `}>
                      <Mic2 className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-1">I'm an Artist</h3>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed">
                        Upload music, build your fanbase, access analytics, 
                        and connect with listeners. Get your Artist Dashboard.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="px-2 py-1 rounded-full bg-[#FF007F]/20 text-[#FF007F] text-xs">
                          Upload Tracks
                        </span>
                        <span className="px-2 py-1 rounded-full bg-[#FF007F]/20 text-[#FF007F] text-xs">
                          Analytics
                        </span>
                        <span className="px-2 py-1 rounded-full bg-[#FF007F]/20 text-[#FF007F] text-xs">
                          Fan Insights
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.button>

                {/* Listener Option */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedRole('listener')}
                  className={`
                    w-full p-5 rounded-2xl border-2 text-left transition-all duration-200
                    ${selectedRole === 'listener' 
                      ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                      : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                    }
                  `}
                  data-testid="role-listener-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0
                      ${selectedRole === 'listener' 
                        ? 'bg-[#00F0FF]' 
                        : 'bg-gradient-to-br from-[#00F0FF]/50 to-[#00F0FF]/20'
                      }
                    `}>
                      <Headphones className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg mb-1">I'm a Listener</h3>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed">
                        Discover new music, support artists, create playlists, 
                        and join the conversation in live rooms.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="px-2 py-1 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-xs">
                          Unlimited Music
                        </span>
                        <span className="px-2 py-1 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-xs">
                          Live Rooms
                        </span>
                        <span className="px-2 py-1 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-xs">
                          Support Artists
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.button>

                {/* Note */}
                <p className="text-[#52525B] text-xs text-center mt-2">
                  You can always change this later or upgrade to Artist anytime.
                </p>
              </div>

              {/* Continue Button */}
              <div className="p-6 pt-0">
                <button
                  onClick={handleContinue}
                  disabled={!selectedRole || loading}
                  className={`
                    w-full py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
                    ${selectedRole 
                      ? selectedRole === 'artist'
                        ? 'bg-[#FF007F] text-white hover:bg-[#FF007F]/90'
                        : 'bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/90'
                      : 'bg-white/10 text-white/50 cursor-not-allowed'
                    }
                  `}
                  data-testid="role-continue-btn"
                >
                  {loading ? (
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-current border-t-transparent rounded-full"
                    />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default RoleSelectionModal;
